import React from "react";

// ============================================================
// HELPERS
// ============================================================

function splitValue(value = "") {
  const parts = value.split("|");

  return {
    title: (parts[0] || "").trim(),
    description: parts.slice(1).join("|").trim(),
  };
}

function parseVisualBlock(lines = []) {
  const data = {};

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) continue;

    const colonIndex = line.indexOf(":");

    if (colonIndex === -1) continue;

    const key = line
      .slice(0, colonIndex)
      .trim();

    const value = line
      .slice(colonIndex + 1)
      .trim();

    // Repeated keys such as LeftItem / RightItem
    if (data[key]) {
      if (!Array.isArray(data[key])) {
        data[key] = [data[key]];
      }

      data[key].push(value);
    } else {
      data[key] = value;
    }
  }

  return data;
}

function normalizeArray(value) {
  if (!value) return [];

  return Array.isArray(value)
    ? value
    : [value];
}

// ============================================================
// NORMAL TEXT
// ============================================================

function NormalText({ text }) {
  const lines = text
    .replace(/\r\n/g, "\n")
    .split("\n");

  return (
    <div className="space-y-4">
      {lines.map((rawLine, index) => {
        const line = rawLine.trim();

        if (!line) {
          return (
            <div
              key={index}
              className="h-2"
            />
          );
        }

        // Numbered report headings
        if (/^\d+\.\s+/.test(line)) {
          return (
            <h3
              key={index}
              className="
                mt-8
                mb-3
                text-xl
                md:text-2xl
                font-bold
                text-[#EB6AB5]
              "
            >
              {line}
            </h3>
          );
        }

        // Phase headings
        if (/^Phase\s+\d+/i.test(line)) {
          return (
            <h4
              key={index}
              className="
                mt-5
                text-lg
                font-semibold
                text-pink-300
              "
            >
              {line}
            </h4>
          );
        }

        // Bullet
        if (
          line.startsWith("-") ||
          line.startsWith("•")
        ) {
          return (
            <div
              key={index}
              className="
                flex
                gap-3
                pl-2
                text-gray-200
                leading-7
              "
            >
              <span className="text-[#EB6AB5]">
                •
              </span>

              <span>
                {line
                  .replace(/^[-•]\s*/, "")
                  .trim()}
              </span>
            </div>
          );
        }

        return (
          <p
            key={index}
            className="
              text-gray-200
              leading-8
              text-[16px]
              md:text-[17px]
            "
          >
            {line}
          </p>
        );
      })}
    </div>
  );
}

// ============================================================
// SKILLS BRIDGE
// ============================================================

function SkillsBridge({ data }) {
  const leftItems =
    normalizeArray(data.LeftItem);

  const rightItems =
    normalizeArray(data.RightItem);

  return (
    <section
      className="
        my-10
        rounded-2xl
        border
        border-pink-500/25
        bg-[#151325]
        p-6
        md:p-8
      "
    >
      <div className="mb-6">
        <span
          className="
            inline-flex
            rounded-full
            border
            border-pink-500/30
            bg-pink-500/10
            px-3
            py-1
            text-xs
            font-semibold
            text-pink-300
          "
        >
          SKILLS TRANSFER
        </span>

        <h3
          className="
            mt-3
            text-2xl
            font-bold
            text-white
          "
        >
          {data.Title ||
            "Skills Transfer Bridge"}
        </h3>

        {data.Intro && (
          <p className="mt-2 text-gray-400">
            {data.Intro}
          </p>
        )}
      </div>

      <div
        className="
          grid
          gap-5
          md:grid-cols-2
        "
      >
        {/* LEFT */}

        <div
          className="
            rounded-xl
            border
            border-white/10
            bg-[#0f0e1d]
            p-5
          "
        >
          <h4
            className="
              mb-4
              text-lg
              font-bold
              text-pink-300
            "
          >
            {data.LeftTitle ||
              "Your Current Skills"}
          </h4>

          <div className="space-y-4">
            {leftItems.map(
              (item, index) => {
                const {
                  title,
                  description,
                } =
                  splitValue(item);

                return (
                  <div
                    key={index}
                    className="
                      rounded-lg
                      border
                      border-white/5
                      bg-white/[0.03]
                      p-4
                    "
                  >
                    <div className="font-semibold text-white">
                      {title}
                    </div>

                    {description && (
                      <div
                        className="
                          mt-1
                          text-sm
                          leading-6
                          text-gray-400
                        "
                      >
                        {description}
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>

        {/* RIGHT */}

        <div
          className="
            rounded-xl
            border
            border-pink-500/20
            bg-pink-500/[0.04]
            p-5
          "
        >
          <h4
            className="
              mb-4
              text-lg
              font-bold
              text-pink-300
            "
          >
            {data.RightTitle ||
              "Target Role Skills"}
          </h4>

          <div className="space-y-4">
            {rightItems.map(
              (item, index) => {
                const {
                  title,
                  description,
                } =
                  splitValue(item);

                return (
                  <div
                    key={index}
                    className="
                      rounded-lg
                      border
                      border-pink-500/10
                      bg-[#171221]
                      p-4
                    "
                  >
                    <div className="font-semibold text-white">
                      {title}
                    </div>

                    {description && (
                      <div
                        className="
                          mt-1
                          text-sm
                          leading-6
                          text-gray-400
                        "
                      >
                        {description}
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>

      {data.Caption && (
        <p
          className="
            mt-5
            text-center
            text-xs
            text-gray-500
          "
        >
          {data.Caption}
        </p>
      )}
    </section>
  );
}

// ============================================================
// PATHWAY CARDS
// ============================================================

function PathwayCards({ data }) {
  const cards = [1, 2, 3]
    .map((number) => ({
      badge:
        data[`Card${number}Badge`],

      title:
        data[`Card${number}Title`],

      sector:
        data[`Card${number}Sector`],

      why:
        data[`Card${number}Why`],

      resilience:
        data[
          `Card${number}Resilience`
        ],

      salary:
        data[`Card${number}Salary`],

      training:
        data[
          `Card${number}Training`
        ],
    }))
    .filter((card) => card.title);

  if (!cards.length) return null;

  return (
    <section className="my-10">

      <div className="mb-6">
        <span
          className="
            inline-flex
            rounded-full
            border
            border-pink-500/30
            bg-pink-500/10
            px-3
            py-1
            text-xs
            font-semibold
            text-pink-300
          "
        >
          CAREER PATHWAYS
        </span>

        <h3
          className="
            mt-3
            text-2xl
            font-bold
            text-white
          "
        >
          {data.Title ||
            "Career Pathway Comparison"}
        </h3>

        {data.Intro && (
          <p className="mt-2 text-gray-400">
            {data.Intro}
          </p>
        )}
      </div>

      <div
        className="
          grid
          gap-5
          lg:grid-cols-3
        "
      >
        {cards.map(
          (card, index) => (
            <div
              key={index}
              className="
                rounded-2xl
                border
                border-pink-500/25
                bg-[#151325]
                p-5
                shadow-lg
              "
            >
              {card.badge && (
                <span
                  className="
                    inline-flex
                    rounded-full
                    bg-pink-500/10
                    px-3
                    py-1
                    text-xs
                    font-semibold
                    text-pink-300
                  "
                >
                  {card.badge}
                </span>
              )}

              <h4
                className="
                  mt-4
                  text-xl
                  font-bold
                  text-white
                "
              >
                {card.title}
              </h4>

              {card.sector && (
                <p
                  className="
                    mt-1
                    text-sm
                    text-pink-300
                  "
                >
                  {card.sector}
                </p>
              )}

              {card.why && (
                <p
                  className="
                    mt-4
                    text-sm
                    leading-6
                    text-gray-300
                  "
                >
                  {card.why}
                </p>
              )}

              <div
                className="
                  mt-5
                  space-y-3
                  border-t
                  border-white/10
                  pt-4
                  text-sm
                "
              >
                {card.resilience && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">
                      AI Resilience
                    </span>

                    <span className="text-white">
                      {card.resilience}
                    </span>
                  </div>
                )}

                {card.salary && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">
                      Salary
                    </span>

                    <span
                      className="
                        text-right
                        text-white
                      "
                    >
                      {card.salary}
                    </span>
                  </div>
                )}

                {card.training && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">
                      Training
                    </span>

                    <span className="text-white">
                      {card.training}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>

      {data.Caption && (
        <p
          className="
            mt-4
            text-center
            text-xs
            text-gray-500
          "
        >
          {data.Caption}
        </p>
      )}
    </section>
  );
}

// ============================================================
// TIMELINE
// ============================================================

function Timeline({ data }) {
  const phases = [1, 2, 3, 4]
    .map((number) => ({
      label:
        data[`Phase${number}Label`],

      weeks:
        data[`Phase${number}Weeks`],

      task:
        data[`Phase${number}Task`],
    }))
    .filter(
      (phase) =>
        phase.label ||
        phase.task
    );

  return (
    <section
      className="
        my-10
        rounded-2xl
        border
        border-pink-500/25
        bg-[#151325]
        p-6
      "
    >
      <h3
        className="
          text-2xl
          font-bold
          text-white
        "
      >
        {data.Title ||
          "Career Transition Timeline"}
      </h3>

      <div className="mt-6 space-y-4">
        {phases.map(
          (phase, index) => (
            <div
              key={index}
              className="
                flex
                gap-4
                rounded-xl
                border
                border-white/10
                bg-[#0f0e1d]
                p-4
              "
            >
              <div
                className="
                  flex
                  h-9
                  w-9
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-[#EB6AB5]
                  font-bold
                  text-[#0F0A24]
                "
              >
                {index + 1}
              </div>

              <div>
                <h4 className="font-bold text-white">
                  {phase.label}
                </h4>

                {phase.weeks && (
                  <p
                    className="
                      mt-1
                      text-sm
                      text-pink-300
                    "
                  >
                    {phase.weeks}
                  </p>
                )}

                {phase.task && (
                  <p
                    className="
                      mt-2
                      leading-6
                      text-gray-300
                    "
                  >
                    {phase.task}
                  </p>
                )}
              </div>
            </div>
          )
        )}
      </div>

      {data.RunwayNote && (
        <div
          className="
            mt-5
            rounded-xl
            border
            border-pink-500/20
            bg-pink-500/[0.05]
            p-4
          "
        >
          <div
            className="
              text-sm
              font-semibold
              text-pink-300
            "
          >
            {data.RunwayLabel ||
              "Financial Runway"}
          </div>

          <p className="mt-2 text-gray-300">
            {data.RunwayNote}
          </p>
        </div>
      )}
    </section>
  );
}

// ============================================================
// SALARY COMPARISON
// ============================================================

function SalaryComparison({ data }) {
  const rows = [1, 2, 3]
    .map((number) => ({
      label:
        data[`Salary${number}Label`],

      min:
        data[`Salary${number}Min`],

      max:
        data[`Salary${number}Max`],
    }))
    .filter((row) => row.label);

  return (
    <section
      className="
        my-10
        rounded-2xl
        border
        border-pink-500/25
        bg-[#151325]
        p-6
      "
    >
      <h3
        className="
          text-2xl
          font-bold
          text-white
        "
      >
        {data.Title ||
          "Salary Comparison"}
      </h3>

      <div className="mt-6 space-y-3">
        {rows.map(
          (row, index) => (
            <div
              key={index}
              className="
                rounded-xl
                border
                border-white/10
                bg-[#0f0e1d]
                p-4
              "
            >
              <div
                className="
                  flex
                  flex-col
                  gap-2
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                "
              >
                <span
                  className="
                    font-semibold
                    text-white
                  "
                >
                  {row.label}
                </span>

                <span
                  className="
                    text-pink-300
                  "
                >
                  {row.min}
                  {row.min &&
                    row.max &&
                    " – "}
                  {row.max}
                </span>
              </div>
            </div>
          )
        )}
      </div>

      {data.ReferenceNote && (
        <p
          className="
            mt-4
            text-sm
            text-gray-500
          "
        >
          {data.ReferenceLabel
            ? `${data.ReferenceLabel}: `
            : ""}

          {data.ReferenceNote}
        </p>
      )}
    </section>
  );
}

// ============================================================
// MAIN PARSER
// ============================================================

export default function CareerReportRenderer({
  report = "",
}) {
  if (!report?.trim()) {
    return null;
  }

  const lines =
    report
      .replace(/\r\n/g, "\n")
      .split("\n");

  const sections = [];

  let textBuffer = [];
  let visualType = null;
  let visualBuffer = [];

  const flushText = () => {
    const text =
      textBuffer
        .join("\n")
        .trim();

    if (text) {
      sections.push({
        type: "text",
        content: text,
      });
    }

    textBuffer = [];
  };

  const flushVisual = () => {
    if (!visualType) return;

    sections.push({
      type: visualType,
      data:
        parseVisualBlock(
          visualBuffer
        ),
    });

    visualType = null;
    visualBuffer = [];
  };

  for (const rawLine of lines) {
    const line =
      rawLine.trim();

    const startMatch =
      line.match(
        /^\[\[visual_([a-z0-9_]+)\]\]$/i
      );

    const endMatch =
      line.match(
        /^\[\[\/visual_([a-z0-9_]+)\]\]$/i
      );

    if (startMatch) {
      flushText();

      visualType =
        startMatch[1]
          .toLowerCase();

      visualBuffer = [];

      continue;
    }

    if (endMatch) {
      flushVisual();

      continue;
    }

    if (visualType) {
      visualBuffer.push(
        rawLine
      );
    } else {
      textBuffer.push(
        rawLine
      );
    }
  }

  flushText();
  flushVisual();

  return (
    <div
      className="
        text-left
        max-w-5xl
        mx-auto
      "
    >
      {sections.map(
        (section, index) => {
          if (
            section.type ===
            "text"
          ) {
            return (
              <NormalText
                key={index}
                text={
                  section.content
                }
              />
            );
          }

          if (
            section.type ===
            "bridge"
          ) {
            return (
              <SkillsBridge
                key={index}
                data={
                  section.data
                }
              />
            );
          }

          if (
            section.type ===
            "pathway_cards"
          ) {
            return (
              <PathwayCards
                key={index}
                data={
                  section.data
                }
              />
            );
          }

          if (
            section.type ===
            "timeline"
          ) {
            return (
              <Timeline
                key={index}
                data={
                  section.data
                }
              />
            );
          }

          if (
            section.type ===
            "salary"
          ) {
            return (
              <SalaryComparison
                key={index}
                data={
                  section.data
                }
              />
            );
          }

          // Never expose raw internal visual metadata.
          return null;
        }
      )}
    </div>
  );
}